import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rocket, Brain, Layout, Award, Briefcase,
  Component, PenTool, Play, Megaphone
} from 'lucide-react';

const paths = [
  {
    id: 'uiux',
    title: 'مصمم واجهات وتجربة مستخدم',
    description: 'تعلم بناء تطبيقات ومواقع جذابة وسهلة الاستخدام عبر فهم سلوك المستخدم وأدوات التصميم الحديثة.',
    icon: <Component className="w-8 h-8" />,
    progress: 75,
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'graphic',
    title: 'مصمم جرافيك محترف',
    description: 'اتقن مبادئ التصميم البصري، بناء الهويات التجارية، وتصميم الإعلانات المبتكرة باستخدام أدوات أدوبي.',
    icon: <PenTool className="w-8 h-8" />,
    progress: 100,
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'motion',
    title: 'موشن جرافر',
    description: 'حول الرسوم الثابتة إلى قصص متحركة مبهرة وتعلم فنون التحريك والمؤثرات البصرية الاحترافية.',
    icon: <Play className="w-8 h-8" />,
    progress: 50,
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'marketing',
    title: 'مسوق رقمي',
    description: 'اكتشف أسرار النمو في العالم الرقمي، من إدارة الحملات الإعلانية إلى استراتيجيات صناعة المحتوى.',
    icon: <Megaphone className="w-8 h-8" />,
    progress: 85,
    color: 'from-green-500 to-emerald-500'
  }
];

const steps = [
  { label: 'ابدأ التعلم', icon: <Rocket className="w-6 h-6" /> },
  { label: 'اكتسب المهارة', icon: <Brain className="w-6 h-6" /> },
  { label: 'نفذ مشاريع', icon: <Layout className="w-6 h-6" /> },
  { label: 'احصل على الشهادة', icon: <Award className="w-6 h-6" /> },
  { label: 'انطلق لسوق العمل', icon: <Briefcase className="w-6 h-6" /> },
];

export default function LearningPathRecommender() {
  const [activeId, setActiveId] = useState(paths[0].id);
  const activePath = paths.find(p => p.id === activeId);

  return (
    <section className="py-16 md:py-24 bg-slate-50 dark:bg-[#050505] relative overflow-hidden transition-colors duration-500" dir="rtl">
      {/* Background Decor */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-primary-500/5 dark:bg-primary-500/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-amber-500/5 dark:bg-amber-500/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-primary-600 dark:text-primary-500 font-bold tracking-[0.2em] uppercase text-sm mb-4 block"
          >
            خارطة طريقك للنجاح
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white mb-4 md:mb-6 leading-tight"
          >
            اكتشف مسارك التعليمي المخصص
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-slate-600 dark:text-zinc-400 text-base md:text-lg font-medium"
          >
            اختر التخصص الذي يستهويك وشاهد رحلة تحولك من مبتدئ إلى محترف في سوق العمل.
          </motion.p>
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {paths.map((path) => {
            const isActive = activeId === path.id;
            return (
              <motion.div
                key={path.id}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveId(path.id)}
                className={`cursor-pointer p-6 md:p-8 rounded-[32px] transition-all duration-500 border-2 relative overflow-hidden group ${isActive
                   ? 'bg-primary-600 text-white dark:bg-white dark:text-zinc-950 border-primary-500 shadow-2xl shadow-primary-500/20'
                   : 'bg-white dark:bg-zinc-900/40 text-slate-500 dark:text-zinc-400 border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:hover:border-zinc-700'
                   }`}
              >
                <div className={`mb-6 transition-colors duration-500 ${isActive ? 'text-white dark:text-primary-500' : 'text-slate-400 dark:text-zinc-500 group-hover:text-slate-600 dark:group-hover:text-zinc-300'}`}>
                  {path.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-inherit' : 'text-slate-900 dark:text-white'}`}>
                  {path.title}
                </h3>
                <p className={`text-md leading-relaxed font-medium transition-colors ${isActive ? 'text-inherit' : 'text-slate-400 dark:text-zinc-500'}`}>
                  {path.description}
                </p>

                {/* Active Glow State */}
                {isActive && (
                  <div className="absolute top-0 right-0 w-24 h-24 bg-primary-500/10 blur-[40px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Timeline Visualization */}
        <div className="mt-12 md:mt-16 bg-white dark:bg-zinc-900/40 backdrop-blur-xl border border-slate-200 dark:border-white/5 rounded-[32px] md:rounded-[40px] p-6 md:p-12 relative shadow-sm dark:shadow-none overflow-hidden">
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-10 md:gap-4">

            {/* Connecting Line (Horizontal - Desktop) */}
            <div className="absolute top-[35px] left-0 right-0 h-1 bg-slate-200 dark:bg-zinc-800 hidden md:block rounded-full z-0">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${activePath.progress}%` }}
                transition={{ duration: 1.5, ease: 'easeInOut' }}
                className="h-full bg-primary-500 relative rounded-full"
              >
                <motion.div
                  initial={{ right: '-20%' }}
                  animate={{ right: '120%' }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                  className="absolute top-0 h-full w-40 bg-gradient-to-l from-transparent via-white/40 to-transparent"
                />
              </motion.div>
              <motion.div
                initial={{ right: 0 }}
                animate={{ right: `${activePath.progress}%` }}
                transition={{ duration: 1.5, ease: 'easeInOut' }}
                className="absolute top-1/2 -translate-y-1/2 -mr-2 w-5 h-5 bg-primary-500 rounded-full border-4 border-white dark:border-zinc-900 shadow-[0_0_20px_rgba(230,0,35,0.7)] z-10"
              />
            </div>

            {/* Connecting Line (Vertical - Mobile) */}
            <div className="absolute top-8 bottom-8 right-1/2 translate-x-1/2 w-1 bg-slate-200 dark:bg-zinc-800 md:hidden rounded-full z-0">
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${activePath.progress}%` }}
                transition={{ duration: 1.5, ease: 'easeInOut' }}
                className="w-full bg-primary-500 relative rounded-full"
              >
                <motion.div
                  initial={{ top: '-20%' }}
                  animate={{ top: '120%' }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                  className="absolute left-0 w-full h-20 bg-gradient-to-b from-transparent via-white/40 to-transparent"
                />
              </motion.div>
            </div>

            {steps.map((step, idx) => {
              const stepProgress = (idx / (steps.length - 1)) * 100;
              const isReached = stepProgress <= activePath.progress;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className={`flex flex-col items-center gap-4 relative z-20 w-full md:w-auto transition-opacity duration-500 ${isReached ? 'opacity-100' : 'opacity-40 grayscale-[0.5]'}`}
                >
                  <motion.div
                    key={activeId}
                    initial={{ scale: 0.8, rotate: -10 }}
                    animate={{ scale: isReached ? 1 : 0.9, rotate: 0 }}
                    whileHover={isReached ? { scale: 1.1, rotate: 5 } : {}}
                    className={`w-[60px] h-[60px] md:w-[70px] md:h-[70px] rounded-2xl flex items-center justify-center transition-all duration-500 border-2 ${isReached
                      ? 'bg-white dark:bg-zinc-900 border-primary-500 text-primary-500 shadow-lg dark:shadow-[0_0_20px_rgba(230,0,35,0.2)]'
                      : 'bg-slate-50 dark:bg-zinc-950 border-slate-200 dark:border-zinc-800 text-slate-400 dark:text-zinc-600'
                      }`}
                  >
                    {step.icon}
                  </motion.div>
                  <span className={`font-bold text-md md:text-lg text-center transition-colors duration-500 ${isReached ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-zinc-600'}`}>
                    {step.label}
                  </span>
                </motion.div>
              );
            })}

          </div>
        </div>
      </div>
    </section>
  );
}
